import { useState, useEffect, useRef } from "react"
import axios from "axios"
import { Controller } from "react-hook-form"
import { Box, Field, Flex } from "@chakra-ui/react"
import {
    AutoComplete,
    AutoCompleteGroup,
    AutoCompleteInput,
    AutoCompleteItem,
    AutoCompleteList,
} from "@choc-ui/chakra-autocomplete"
import useDebounce from "lib/useDebounce"

export default function AutoCompleteField({
    control,
    rules,
    name,
    value,
    options,
    label,
    help,
    adapter,
    showEmpty,
    params,
}) {
    const [opts, setOpts] = useState(typeof options === "string" ? [] : options)
    const [inputValue, setInputValue] = useState("")
    const debouncedValue = useDebounce(inputValue, 500)
    const inputItemRef = useRef()

    useEffect(() => {
        // do not fire on first mount
        if (debouncedValue.length > 2) {
            getOptions(debouncedValue)
        } else {
            setOpts([])
        }
    }, [debouncedValue])

    // Sync inputValue with field value to handle external changes
    useEffect(() => {
        if (value !== undefined && value !== inputValue) {
            setInputValue(value || "")
        }
    }, [value])

    // request new options from the endpoint based on the selected value
    const getOptions = async search => {
        const { data: rawOptions } = await axios.get(options.replace("[[input]]", encodeURIComponent(search)))
        let _opts = []
        if (adapter) {
            _opts = adapter(rawOptions)
        } else {
            _opts = rawOptions
        }
        setOpts(_opts)
    }

    // hide any select field with no options
    // if (typeof options === "object" && !options.length && !showEmpty) return null

    return (
        <Controller
            name={name}
            control={control}
            defaultValue={value || ""}
            rules={rules}
            render={({ field, fieldState: { error } }) => {
                return (
                    <Box className="chakra-autocomplete" w="full">
                        <Field.Root invalid={!!error}>
                            <Field.Label htmlFor={name}>{label}</Field.Label>
                            <AutoComplete
                                rollNavigation
                                value={field.value}
                                onChange={val => {
                                    // Handle both selection and clearing
                                    const newValue = typeof val === "string" ? val : val?.title || ""
                                    field.onChange(newValue)
                                    setInputValue(newValue)
                                }}
                                emphasize={true}
                                emptyState={true}
                                {...params}
                                prefocusFirstItem={false}
                                selectOnBlur={false}
                                selectOnFocus={false}
                                restoreOnBlurIfEmpty={false}
                                freeSolo={true}
                                display="block"
                                // onChange={val => setValue("team", val)}
                                // listAllValuesOnFocus
                            >
                                <AutoCompleteInput
                                    ref={inputItemRef}
                                    variant="subtle"
                                    name={name}
                                    value={field.value || ""}
                                    onChange={e => {
                                        const newValue = e.target.value
                                        field.onChange(newValue)
                                        setInputValue(newValue)
                                    }}
                                    bgColor="white"
                                    border={"1px solid"}
                                    borderColor={error ? "red.300" : "gray.200!important"}
                                    autoComplete="off"
                                />
                                <AutoCompleteList display={opts.length > 0 ? "block" : "none"}>
                                    {opts.map(option => (
                                        <AutoCompleteItem
                                            key={`option-${option.value}`}
                                            className="chakra-autocomplete__item"
                                            value={{
                                                title: `${option.value}`,
                                            }}
                                            label={option.label}
                                            color="gray.800"
                                            _selected={{
                                                bg: "blue.100",
                                                color: "blue.800",
                                            }}
                                        />
                                    ))}
                                </AutoCompleteList>
                            </AutoComplete>
                            {help && <Field.HelperText>{help}</Field.HelperText>}
                            {error && <Field.ErrorText>{error.message}</Field.ErrorText>}
                        </Field.Root>
                    </Box>
                )
            }}
        />
    )
}
